import { Component, OnDestroy, OnInit } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, take, tap } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';
import {
  CoupledEmployee,
  DelegatedTeam,
  StudentRoleSettings,
  UserLevelEnum,
} from 'src/app/auth/models/UserData';
import { AuthService } from 'src/app/auth/services/auth.service';
import { UserService } from 'src/app/auth/services/user.service';
import { HomepageService } from 'src/app/modules/framework/home-pages/employee-home-page/services/homepage.service';
import { HomepageSetting } from 'src/app/modules/framework/home-pages/employee-home-page/models/HomepageSettings';
import { Student } from 'src/app/modules/lms/learningpaths/models/interfaces';
import { LabelContext } from 'src/app/shared/framework/labels/models/LabelContext';
import { LabelService } from 'src/app/shared/framework/labels/services/label.service';
import {
  BreadCrumbNavigation,
  MenuItem,
} from 'src/app/shared/framework/navigation/models/MenuItem';
import { MenuItemFavourite } from 'src/app/shared/framework/navigation/models/MenuItemFavourite';
import { EmployeeService } from 'src/app/shared/personnel-files/services/employee.service';
import { BreadcrumbService } from '../../services/breadcrumb.service';
import { NavigationService } from 'src/app/shared/framework/navigation/services/navigation.service';

@Component({
  selector: 'app-bread-crumb-navigation',
  templateUrl: './bread-crumb-navigation.component.html',
  styleUrls: ['./bread-crumb-navigation.component.scss'],
})
export class BreadCrumbNavigationComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription[] = [];

  public roles$: Observable<StudentRoleSettings>;
  public currentEmployee$: Observable<Student>;
  public navigationStructure$: Observable<MenuItem>;
  public navigationStructure: MenuItem;
  public breadCrumbStructure: BreadCrumbNavigation;
  public greetingLabel = 'greeting_morning';
  public roleLabel = '';
  public birthdayToday: boolean;

  public Roles = UserLevelEnum;
  public noActiveMenuItemFound: boolean;
  public activeMenuItem: MenuItem;
  public showFavourites: boolean;
  public favourites: MenuItemFavourite[] = [];
  public isFavourite = false;
  public isMatrixManager = false;
  public labelContext: LabelContext;
  public eventsSubject: Subject<void> = new Subject<void>();
  public activeFavourite: MenuItemFavourite;
  public favouriteName$ = new Subject<string>();

  constructor(
    private _navigationService: NavigationService,
    private _breadcrumbService: BreadcrumbService,
    private _homepageService: HomepageService,
    private _employeeService: EmployeeService,
    private _userService: UserService,
    private _authService: AuthService,
    private _labelService: LabelService,
    private toastr: ToastrService,
    private _router: Router
  ) {
    const d = new Date();
    if (d.getHours() >= 5 && d.getHours() < 12) {
      this.greetingLabel = 'greeting_morning';
    } else if (d.getHours() >= 12 && d.getHours() < 14) {
      this.greetingLabel = 'greeting_noon';
    } else if (d.getHours() >= 14 && d.getHours() < 17) {
      this.greetingLabel = 'greeting_afternoon';
    } else if (d.getHours() >= 16) {
      this.greetingLabel = 'greeting_evening';
    }
  }

  public async ngOnInit() {
    this.labelContext = await firstValueFrom(this._labelService.getContext('navigation'));

    this.setupNavigationEventListener();
    this.navigationStructure$ = this._navigationService.fetchMenu();

    this._subscriptions.push(
      this._breadcrumbService.url$.subscribe((url) => {
        if (url && url.length) {
          this.extractMenuItem(url);
        }
      })
    );

    this._subscriptions.push(
      this._homepageService
        .getSettings(false)
        .subscribe((settings: HomepageSetting[]) => {
          if (localStorage.getItem('delegate_view') === '1') {
            this.showFavourites = settings.filter(e => e.isVisible && e.roleId == 333 && e.name == 'show_favourites')[0]?.value;
          } else if (localStorage.getItem('manager_view') === '1') {
            this.showFavourites = settings.filter(e => e.isVisible && e.roleId == 33 && e.name == 'show_favourites')[0]?.value;
          } else if (localStorage.getItem('trainer_view') === '1') {
            this.showFavourites = settings.filter(e => e.isVisible && e.roleId == 6 && e.name == 'show_favourites')[0]?.value;
          } else if (localStorage.getItem('matrixmanager_view') === '1') {
            this.showFavourites = settings.filter(e => e.isVisible && e.roleId == 33 && e.name == 'show_favourites')[0]?.value;
          } else if (localStorage.getItem('localhr_view') === '1') {
            this.showFavourites = settings.filter(e => e.isVisible && e.roleId == 2 && e.name == 'show_favourites')[0]?.value;
          } else if (localStorage.getItem('corporatehr_view') === '1') {
            this.showFavourites = settings.filter(e => e.isVisible && e.roleId == 1 && e.name == 'show_favourites')[0]?.value;
          } else {
            this.showFavourites = settings.filter(e => e.isVisible && e.roleId == 3 && e.name == 'show_favourites')[0]?.value;
          }

          if (this.showFavourites) {
            this._subscriptions.push(
              this._navigationService.getFavourites().subscribe((favourites) => {
                this.favourites = favourites;
                if (this.isFavourite) {
                  this.activeFavourite = this.favourites.filter(e => e.menuItemId == this.activeMenuItem.id)[0];
                }
              })
            );
          }
        })
    );

    this._subscriptions.push(
      this.navigationStructure$
        .pipe(take(1))
        .subscribe((menuItem: MenuItem) => {
          this.navigationStructure = menuItem;
          this.extractMenuItem(this._router.url);
        })
    );

    this.currentEmployee$ = this._employeeService.getCurrentEmployee().pipe(
      tap((e) => {

        const d = new Date();
        d.setHours(0, 0, 0, 0);
        e.birthday = new Date(e.birthday);
        if (e.birthday.getMonth() == d.getMonth() && e.birthday.getDate() == d.getDate()) {
          this.greetingLabel = 'greeting_birthday';
          this.birthdayToday = true;
        }
        this.eventsSubject.next();
      })
    );

    this.roles$ = this._userService.getUserRoles().pipe(
      tap((roles) => {
        if (roles && roles.level) {
          let role = this.Roles[roles.level];
          if (roles.isManager && localStorage.getItem('manager_view') === '1') {
            role = 'manager';
          }
          if (roles.isMatrixManager && localStorage.getItem('matrixmanager_view') === '1') {
            role = 'matrix_manager';
            this.isMatrixManager = true;
          }
          if (roles.delegatedTeams != null && roles.delegatedTeams.length && localStorage.getItem('delegate_view') === '1') {
            role = 'manager';
          }
          this.roleLabel = role;

          this.eventsSubject.next();
        }
      })
    );

    this._subscriptions.push(
      this.favouriteName$.pipe(debounceTime(750), distinctUntilChanged()).subscribe(() => {
        this._subscriptions.push(this._navigationService.updateFavouriteLabel(this.activeFavourite).subscribe(() => {
          this.toastr.success(this.labelContext.get('favourite_name_saved').labelText, undefined, { timeOut: 1000 });
        }));
      })
    );
  }

  public hasMultipleRoles(roles: StudentRoleSettings): boolean {
    return (
      roles.studentId !== null &&
      (roles.isManager || roles.isMatrixManager ||
        (roles.coupledEmployees !== null && roles.coupledEmployees.length > 0) ||
        (roles.delegatedTeams !== null && roles.delegatedTeams.length > 0) ||
        roles.localHrId !== null ||
        roles.trainerId !== null ||
        roles.corporateHrId !== null)
    );
  }

  public isInDelegatedRole(): DelegatedTeam {
    return JSON.parse(localStorage.getItem('delegate'));
  }

  public logOff(): void {
    this._authService.logOff();
  }

  public changeToEmployee(): void {
    this._authService.changeToEmployee();
  }

  public changeToManager(): void {
    this._authService.changeToManager();
  }

  public changeToDelegate(team: DelegatedTeam): void {
    this._authService.changeToDelegate(team);
  }

  public changeToCoupledEmployee(coupledEmployee: CoupledEmployee): void {
    this._authService.changeToCoupledEmployee(coupledEmployee);
  }

  public changeToLocalHr(): void {
    this._authService.changeToLocalHr();
  }

  public changeToCorporateHr(): void {
    this._authService.changeToCorporateHr();
  }

  public changeToMatrixManager(): void {
    this._authService.changeToMatrixManager()
  }

  public changeToTrainer(): void {
    this._authService.changeToTrainer();
  }

  public addFavourite() {
    this.isFavourite = true;
    this._subscriptions.push(this._navigationService.addFavourite(this.activeMenuItem.id, this.activeMenuItem.label).subscribe());
  }

  public removeFavourite() {
    this.isFavourite = false;
    this._subscriptions.push(this._navigationService.removeFavourite(this.activeMenuItem.id).subscribe());
  }

  public updateMenuFavouriteLabel() {
    setTimeout(() => {
      this.favouriteName$.next(this.activeFavourite.label);
    })
  }

  public getUserColor() {
    switch (this.roleLabel) {
      case 'Employee': return 'var(--progreso-accent)';
      case 'manager': return 'var(--progreso-accent-hover)';
      case 'matrix_manager': return 'var(--progreso-accent-hover)';
      case 'Trainer': return 'var(--progreso-primary)';
      case 'LocalHr': return 'var(--progreso-secondary)';
      case 'CorporateHr': return 'var(--progreso-secondary)';
      default: return 'var(--progreso-accent)';
    }
  }

  private setupNavigationEventListener(): void {
    this._subscriptions.push(
      this._router.events
        .pipe(
          filter((event: Event) => event instanceof NavigationEnd),
          tap((event: Event) => {
            this.extractMenuItem((event as NavigationEnd).url);
            this.eventsSubject.next();
          })
        )
        .subscribe()
    );
  }

  private formatUrl(url: string): string {
    let formattedUrl = url.replace('/', '');
    if (formattedUrl.indexOf('?') > -1) {
      formattedUrl = formattedUrl.slice(0, formattedUrl.indexOf('?'));
    }

    const parts = formattedUrl.split('/');
    if (new RegExp(/^\d+$/).test(parts[parts.length - 1])) {
      parts.pop();
      parts.push(':id');
      formattedUrl = parts.join('/');
    }

    return formattedUrl;
  }

  private extractMenuItem(url: string): void {
    let param = getParameterByName('menuItem', url);
    const formattedUrl = this.formatUrl(url);
    this._resetMenuItems([this.navigationStructure]);
    const activeMenuItem = this.containsUrl(
      formattedUrl,
      this.navigationStructure,
      (!param ? null : parseInt(param))
    );

    if (activeMenuItem != null) {
      this.noActiveMenuItemFound = false;
      this.breadCrumbStructure = {
        levels: [],
      };

      this.activeMenuItem = activeMenuItem;
      this.isFavourite = this.favourites.filter(e => e.menuItemId == this.activeMenuItem.id).length > 0;

      if (this.isFavourite) {
        this.activeFavourite = this.favourites.filter(e => e.menuItemId == this.activeMenuItem.id)[0];
      }

      this.addBreadCrumbLevel([this.navigationStructure]);
      this.breadCrumbStructure.levels =
        this.breadCrumbStructure.levels.reverse();
    } else {
      this.noActiveMenuItemFound = true;
    }
  }

  private addBreadCrumbLevel(menuItems: MenuItem[]): void {
    let allSubPages: MenuItem[] = [];
    let activeTrailMenuItem: MenuItem;
    let activeMenuItem: MenuItem;

    for (let i = 0; i < menuItems.length; i++) {
      const menuItem = menuItems[i];

      if (!menuItem) {
        continue;
      }

      if (menuItem.isActive) {
        activeMenuItem = menuItem;
        break;
      }

      if (menuItem.hasActiveTrail) {
        activeTrailMenuItem = menuItem;

        if (menuItem.subPages && menuItem.subPages.length) {
          allSubPages = allSubPages.concat(menuItem.subPages);
          break;
        }
      }
    }

    if (!activeMenuItem && allSubPages && allSubPages.length) {
      this.addBreadCrumbLevel(allSubPages);
    }

    if (activeMenuItem) {
      this.breadCrumbStructure.levels.push({
        menuItems: menuItems,
        activeMenuItem: activeMenuItem,
      });
    } else {
      this.breadCrumbStructure.levels.push({
        menuItems: menuItems,
        activeMenuItem: activeTrailMenuItem,
      });
    }
  }

  private _resetMenuItems(menuItems: MenuItem[]): void {
    for (let i = 0; i < menuItems.length; i++) {
      const menuItem = menuItems[i];

      if (!menuItem) {
        continue;
      }

      menuItem.isActive = false;
      menuItem.hasActiveTrail = false;
      menuItem.showChildren = false;
      if (menuItem.subPages) {
        this._resetMenuItems(menuItem.subPages);
      }
    }
  }

  private containsUrl(formattedUrl: string, menuItem: MenuItem, menuItemId?: number, menuItemInParent = false): MenuItem {
    if (!menuItem) {
      return null;
    }

    const itemFormattedUrl = menuItem.url.substring(1);

    if (menuItem && (formattedUrl === itemFormattedUrl || formattedUrl.replace("/:id", "") === itemFormattedUrl) && (!menuItemId || menuItemId == menuItem.id || menuItemInParent)) {
      menuItem.isActive = true;
      return menuItem;
    }

    for (let i = 0; menuItem.subPages && i < menuItem.subPages.length; i++) {
      const subItem: MenuItem = menuItem.subPages[i];

      if (!subItem) {
        continue;
      }

      menuItemInParent = !!menuItemId && (menuItemInParent || menuItem.id == menuItemId);
      const activeMenuItem = this.containsUrl(formattedUrl, subItem, menuItemId, menuItemInParent);
      if (activeMenuItem != null) {
        menuItem.subPages[i].hasActiveTrail = true;
        menuItem.hasActiveTrail = true;
        return activeMenuItem;
      }
    }
    return null;
  }

  ngOnDestroy(): void {
    for (const subscr of this._subscriptions) {
      subscr.unsubscribe();
    }
  }
}

function getParameterByName(name, url = window.location.href) {
  name = name.replace(/[\[\]]/g, '\\$&');
  var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}
